import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface GAConfig {
  enabled: boolean;
  measurement_id: string;
}

const SETTING_KEY = 'google_analytics_config';

export default function GoogleAnalyticsConfigTab() {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [measurementId, setMeasurementId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['platform_settings', SETTING_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      return (data?.value as unknown as GAConfig) ?? null;
    },
  });

  useEffect(() => {
    if (data) {
      setEnabled(data.enabled ?? false);
      setMeasurementId(data.measurement_id ?? '');
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const value = { enabled, measurement_id: measurementId.trim() };
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: SETTING_KEY, value: value as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTING_KEY] });
      toast({ title: 'Configuración de Google Analytics guardada' });
    },
    onError: () => {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    },
  });

  const isValidId = !measurementId.trim() || /^G-[A-Z0-9]+$/i.test(measurementId.trim());

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Analytics</CardTitle>
        <CardDescription>
          Configura el Measurement ID de Google Analytics (gtag.js) para rastrear el tráfico de la plataforma.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={setEnabled} />
          <Label>Habilitar Google Analytics</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ga-id">Measurement ID</Label>
          <Input
            id="ga-id"
            placeholder="G-XXXXXXXXXX"
            value={measurementId}
            onChange={e => setMeasurementId(e.target.value)}
            className="max-w-xs"
          />
          {!isValidId && (
            <p className="text-sm text-destructive">
              El formato debe ser G- seguido de caracteres alfanuméricos (ej: G-ABC123DEF4)
            </p>
          )}
        </div>

        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !isValidId}
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar
        </Button>
      </CardContent>
    </Card>
  );
}
